import { useState, useEffect, useRef } from 'react'
import { deleteDataAPI, getDataAPI, postDataAPI, putDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'
import { useLocation } from 'react-router-dom'

interface Song {
  id: string
  song_title?: string
  title?: string
  primary_artist?: string
  artist?: string
  featured_artists?: string
  master_contributors?: { name: string; role?: string; percentage?: number; advance_fee?: number }[]
  publishing_contributors?: { name: string; role?: string; percentage?: number; pro?: string; ipi_number?: string; publishing_line?: string }[]
  isrc?: string
  upc?: string
  duration?: string | number
  bpm?: number
  key?: string
  genre?: string
  sub_genre?: string
  track_type?: string
  language?: string
  explicit?: string
  album_name?: string
  album_title?: string
  label?: string
  distributor?: string
  artwork?: string
  audio_file?: string
  spotify_id?: string
  spotify_link?: string
  artist_id?: string
  soundcharts_data?: {
    song_uuid?: string
    artist_uuid?: string
    streams?: number
    monthly_listeners?: number
    date?: string
    platform?: string
    type?: string
  }
  streams_total?: number
  streams_manual_override?: number
  streams_today?: number
  streams_week?: number
  status?: string
  release_date?: string
  songwriter?: string
  producer?: string
  suggested_writers?: { name: string; role?: string; source?: string }[]
  split_status?: string
}

/** Spotify icon from legacy src/svg/MusicPlatform/Spotify.jsx */
function SpotifyBadge() {
  return (
    <span className="inline-flex flex-shrink-0 self-center" title="From Spotify">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <path
          fill="#1DB954"
          d="M7.477 0a7.477 7.477 0 1 0 0 14.953A7.477 7.477 0 0 0 7.477 0Zm3.429 10.784a.466.466 0 0 1-.641.154c-1.756-1.072-3.966-1.315-6.568-.72a.466.466 0 0 1-.208-.91c2.849-.65 5.292-.37 7.262.835.22.135.29.421.155.64Zm.915-2.036a.583.583 0 0 1-.802.192c-2.01-1.235-5.073-1.593-7.45-.872a.584.584 0 0 1-.34-1.115c2.716-.824 6.092-.425 8.4.993.274.17.36.528.192.802Zm.079-2.12c-2.41-1.432-6.386-1.563-8.687-.865a.7.7 0 1 1-.406-1.338c2.641-.802 7.032-.647 9.806 1a.699.699 0 1 1-.713 1.203Z"
        />
      </svg>
    </span>
  )
}

export default function MusicPage() {
  const location = useLocation()
  const debugStreams = false
  const catalogCacheKey = 'musicPageCatalogCache'
  const [songs, setSongs] = useState<Song[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRefreshingStreams, setIsRefreshingStreams] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editCatalogId, setEditCatalogId] = useState<string | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null)
  const [creditsSongId, setCreditsSongId] = useState<string | null>(null)
  const [addView, setAddView] = useState<'options' | 'manual' | 'single' | 'bulk' | 'credits'>('options')
  const [singleQuery, setSingleQuery] = useState('')
  const [singleResults, setSingleResults] = useState<any[]>([])
  const [singleSelected, setSingleSelected] = useState<any | null>(null)
  const [singleImporting, setSingleImporting] = useState(false)
  const [bulkArtistName, setBulkArtistName] = useState('')
  const [bulkSpotifyUrl, setBulkSpotifyUrl] = useState('')
  const [bulkArtistResults, setBulkArtistResults] = useState<any[]>([])
  const [bulkArtistSearching, setBulkArtistSearching] = useState(false)
  const [bulkArtistSelected, setBulkArtistSelected] = useState<any | null>(null)
  const [bulkResults, setBulkResults] = useState<any[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkImporting, setBulkImporting] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentBatch: 0, totalBatches: 0 })
  const [bulkPhase, setBulkPhase] = useState<'saving' | 'updating' | 'done' | null>(null)
  const [discoveryProgress, setDiscoveryProgress] = useState({ percentage: 0, isActive: false, message: 'Ready to discover...' })
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [fetchingMusoCredits, setFetchingMusoCredits] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedSongIds, setSelectedSongIds] = useState<Set<string>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  // Mirrors legacy Catalog/ManualAddCatalog.jsx fields
  const [editData, setEditData] = useState<Record<string, any>>({
    song_title: '',
    status: 'unreleased',
    release_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    duration: '',
    bpm: '',
    key: '',
    genre: '',
    track_type: '',
    language: '',
    explicit: 'No',
    isrc: '',
    artwork: '',
    audio_file: '',
    label: '',
    distributor: '',
    spotify_link: '',
    apple_music_link: '',
    youtube_link: '',
    sound_cloud_link: '',
    internal_notes: '',
    registered_to_pro: false,
    primary_artists: [''],
    featured_artists: [''],
  })
  const [masterContributors, setMasterContributors] = useState<
    { name: string; role: string; percentage: string; advanceFee: string }[]
  >([{ name: '', role: 'Writer', percentage: '', advanceFee: '' }])
  const [publishingContributors, setPublishingContributors] = useState<
    { name: string; role: string; percentage: string; pro: string; ipiNumber: string; publishingLine: string }[]
  >([{ name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }])
  const [suggestedWritersForEdit, setSuggestedWritersForEdit] = useState<
    { name: string; role: string; source?: string }[]
  >([])

  const normalizeCreditName = (name?: string) => String(name || '').toLowerCase().trim()

  const requiredFields = [
    { key: 'song_title', label: 'Song Title', required: true },
    {
      key: 'status',
      label: 'Release Status',
      type: 'select',
      options: [
        { label: 'Upcoming', value: 'unreleased' },
        { label: 'Past', value: 'released' },
        { label: 'Demo', value: 'demo' },
      ],
      required: true,
    },
    { key: 'release_date', label: 'Release Date', type: 'date', required: true },
  ] as const

  const optionalFields = [
    { key: 'duration', label: 'Duration (minutes:seconds)', placeholder: 'e.g., 3:45' },
    { key: 'bpm', label: 'BPM', type: 'number' },
    { key: 'key', label: 'Key' },
    { key: 'genre', label: 'Genre' },
    { key: 'track_type', label: 'Track Type' },
    { key: 'language', label: 'Language' },
    { key: 'explicit', label: 'Explicit Content', type: 'select', options: ['No', 'Yes'] },
    { key: 'isrc', label: 'ISRC', placeholder: 'Enter ISRC code' },
    { key: 'artwork', label: 'Artwork URL' },
    { key: 'audio_file', label: 'Audio File URL' },
    { key: 'label', label: 'Record Label' },
    { key: 'distributor', label: 'Distributor' },
    { key: 'registered_to_pro', label: 'Registered to PRO', type: 'checkbox' },
    { key: 'spotify_link', label: 'Spotify Link' },
    { key: 'apple_music_link', label: 'Apple Music Link' },
    { key: 'youtube_link', label: 'YouTube Link' },
    { key: 'sound_cloud_link', label: 'SoundCloud Link' },
    { key: 'internal_notes', label: 'Internal Notes', type: 'textarea' },
  ] as const

  const renderField = (field: any) => {
    const value = editData[field.key] ?? ''
    const common = 'ds-input'

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          placeholder={field.placeholder}
          className={`${common} min-h-[90px]`}
        />
      )
    }

    if (field.type === 'select') {
      const opts = field.options || []
      return (
        <select
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          className={common}
        >
          {opts.map((opt: any) => {
            const v = typeof opt === 'object' ? opt.value : opt
            const l = typeof opt === 'object' ? opt.label : opt
            return (
              <option key={v} value={v}>
                {l}
              </option>
            )
          })}
        </select>
      )
    }

    if (field.type === 'date') {
      return (
        <DatePicker
          value={value}
          onChange={(newValue) => setEditData((prev) => ({ ...prev, [field.key]: newValue }))}
          placeholder="yyyy-mm-dd"
          required={!!field.required}
        />
      )
    }

    const inputType = field.type === 'number' ? 'number' : 'text'
    return (
      <input
        type={inputType}
        value={value}
        required={!!field.required}
        onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
        placeholder={field.placeholder}
        className={common}
      />
    )
  }

  const toOptionalNumber = (value: any) => {
    if (value === null || value === undefined || value === '') return undefined
    const num = Number(value)
    return Number.isFinite(num) ? num : undefined
  }

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  // Local calendar day key (avoids UTC off-by-one from `toISOString()`).
  const formatLocalDateKey = (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  const extractStreamCountsFromSeries = (payload: any, identifier?: string) => {
    const data = payload?.data?.data ?? payload?.data ?? payload
    if (!data) return null

    const candidates = [
      data.daily_data,
      data.daily,
      data.daily_streams,
      data.dailyStreams,
      data.streams_daily,
      data.streamsDaily,
      data.history,
      data.data,
      data.plots,
      data.plot,
      data.values,
      data.items,
      data.results,
    ]

    const series = candidates.find((entry) => Array.isArray(entry)) as any[] | undefined
    if (!series?.length) return null

    const toDate = (value: any) => {
      if (!value) return null
      if (value instanceof Date) return value
      if (typeof value === 'number') {
        const ms = value > 1e12 ? value : value * 1000
        return new Date(ms)
      }
      if (typeof value === 'string') {
        // Treat YYYY-MM-DD as a local date (JS parses it as UTC otherwise).
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [yy, mm, dd] = value.split('-').map((x) => Number(x))
          if (Number.isFinite(yy) && Number.isFinite(mm) && Number.isFinite(dd)) {
            return new Date(yy, mm - 1, dd)
          }
        }
        return new Date(value)
      }
      return null
    }

    const selectPlotValue = (plots: any[]) => {
      if (!Array.isArray(plots) || plots.length === 0) return undefined
      const match = identifier
        ? plots.find((plot) => plot?.identifier === identifier)
        : null
      const plot = match || plots.find((p) => p?.value != null) || plots[0]
      return (
        plot?.value ??
        plot?.streams ??
        plot?.stream ??
        plot?.count ??
        plot?.listeners ??
        plot?.y
      )
    }

    const normalized = series
      .map((entry) => {
        if (entry === null || entry === undefined) return null
        if (typeof entry === 'object' && Array.isArray(entry.plots)) {
          const plotValue = selectPlotValue(entry.plots)
          const value = toOptionalNumber(plotValue)
          if (value === undefined) return null
          const rawDate =
            entry.date ?? entry.day ?? entry.timestamp ?? entry.time ?? entry.label ?? entry.x
          return {
            date: toDate(rawDate),
            value,
          }
        }
        const rawValue =
          typeof entry === 'number' || typeof entry === 'string'
            ? entry
            : entry.streams ??
              entry.stream ??
              entry.streams_count ??
              entry.count ??
              entry.listeners ??
              entry.value ??
              entry.y ??
              entry.total ??
              entry.total_streams ??
              entry.totalStreams ??
              entry.daily_streams ??
              entry.dailyStreams ??
              entry.audience
        const value = toOptionalNumber(rawValue)
        if (value === undefined) return null
        const rawDate =
          typeof entry === 'object'
            ? entry.date ??
              entry.day ??
              entry.timestamp ??
              entry.time ??
              entry.label ??
              entry.x
            : null
        return {
          date: toDate(rawDate),
          value,
        }
      })
      .filter(Boolean) as { date: Date | null; value: number }[]

    if (!normalized.length) return null

    const hasDates = normalized.some(
      (entry) => entry.date instanceof Date && !Number.isNaN(entry.date?.getTime())
    )
    if (!hasDates) {
      const streamsToday = normalized[normalized.length - 1].value
      const streamsWeek = normalized.slice(-7).reduce((sum, entry) => sum + entry.value, 0)
      return { streams_today: streamsToday, streams_week: streamsWeek }
    }

    const dated = normalized
      .filter((entry) => entry.date instanceof Date && !Number.isNaN(entry.date?.getTime()))
      .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0))

    if (!dated.length) return null

    const formatKey = (date: Date) => formatLocalDateKey(date)
    const todayKey = formatKey(new Date())
    const lastEntry = dated[dated.length - 1]

    // Heuristic: treat as cumulative only when it very strongly looks cumulative.
    // Daily stream series can be monotonic by coincidence over short windows, which breaks "today/week".
    const nonDecreasingCount = dated.reduce((count, entry, idx) => {
      if (idx === 0) return count
      return entry.value >= dated[idx - 1].value ? count + 1 : count
    }, 0)
    const monotonicRatio = dated.length > 1 ? nonDecreasingCount / (dated.length - 1) : 1
    const firstEntry = dated[0]
    const growth = firstEntry ? Math.max(0, lastEntry.value - firstEntry.value) : 0
    const isCumulative = monotonicRatio >= 0.95 && growth > 0

    const pickEntryForDate = (targetKey: string) =>
      dated.find((entry) => entry.date && formatKey(entry.date as Date) === targetKey)

    const todayEntry = pickEntryForDate(todayKey) || lastEntry
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const yesterdayKey = formatKey(yesterday)
    const yesterdayEntry = pickEntryForDate(yesterdayKey)

    const weekStart = new Date()
    weekStart.setHours(0, 0, 0, 0)
    weekStart.setDate(weekStart.getDate() - 6)
    const weekStartKey = formatKey(weekStart)
    const weekStartEntry = pickEntryForDate(weekStartKey)

    if (isCumulative) {
      const prevEntry = yesterdayEntry || dated[dated.length - 2] || todayEntry
      const streamsToday = Math.max(0, todayEntry.value - prevEntry.value)
      const baseEntry = weekStartEntry || dated[Math.max(0, dated.length - 7)] || todayEntry
      const streamsWeek = Math.max(0, todayEntry.value - baseEntry.value)
      return { streams_today: streamsToday, streams_week: streamsWeek, streams_total: todayEntry.value }
    }

    const streamsToday = todayEntry.value
    const streamsWeek = dated
      .filter((entry) => (entry.date as Date) >= weekStart)
      .reduce((sum, entry) => sum + entry.value, 0)

    return { streams_today: streamsToday, streams_week: streamsWeek }
  }

  const normalizeCatalog = (payload: any) => {
    const list =
      payload?.data?.data ||
      payload?.data ||
      payload ||
      []
    const array = Array.isArray(list) ? list : []
    return array.map((item: any) => {
      const rawStreamsTotal =
        item.streams_total ??
        item.streams ??
        item.streamsTotal ??
        item?.soundcharts_data?.streams
      const rawStreamsToday =
        item.streams_today ??
        item.streamsToday ??
        item.streams_daily ??
        item.streamsDaily ??
        item.daily_streams ??
        item.dailyStreams
      const rawStreamsWeek =
        item.streams_week ??
        item.streamsWeek ??
        item.streams_weekly ??
        item.streamsWeekly ??
        item.weekly_streams ??
        item.weeklyStreams
      return ({
      id: item._id || item.id,
      song_title: item.song_title || item.title || item.songName,
      title: item.title || item.song_title,
      primary_artist: item.primary_artist || item.artist_name || item.artist || item.primaryArtist,
      artist: item.artist || item.artist_name || item.primary_artist,
      featured_artists: item.featured_artists || item.featuredArtists,
      master_contributors: item.master_contributors || item.masterContributors,
      publishing_contributors: item.publishing_contributors || item.publishingContributors,
      suggested_writers: item.suggested_writers || [],
      split_status: item.split_status || '',
      isrc: item.isrc || item.ISRC,
      upc: item.upc || item.UPC,
      duration: item.duration || item.track_duration || item.trackDuration,
      bpm: item.bpm,
      key: item.key,
      genre: item.genre,
      sub_genre: item.sub_genre || item.subGenre,
      track_type: item.track_type || item.trackType,
      language: item.language,
      explicit: item.explicit,
      album_name: item.album_name || item.albumName,
      album_title: item.album_title || item.albumTitle,
      label: item.label || item.record_label || item.recordLabel,
      distributor: item.distributor,
      artwork: item.artwork || item.album_artwork || item.albumArtwork,
      audio_file: item.audio_file || item.audioFile,
      spotify_id: item.spotify_id || item.spotifyId,
      spotify_link: item.spotify_link || item.spotifyUrl || item.spotify_url,
      artist_id: item.artist_id || item.spotify_artist_id || item.spotifyArtistId,
      soundcharts_data: item.soundcharts_data,
      streams_total: toOptionalNumber(rawStreamsTotal),
      streams_manual_override: item.streams_manual_override ?? item.streamsManualOverride,
      streams_today: toOptionalNumber(rawStreamsToday),
      streams_week: toOptionalNumber(rawStreamsWeek),
      status: item.status,
      release_date: item.release_date || item.releaseDate,
      songwriter: item.songwriter,
      producer: item.producer,
    })
    })
  }

  const parseSpotifyId = (url: string, type: 'track' | 'artist') => {
    if (!url) return ''
    const match = url.match(new RegExp(`${type}/([a-zA-Z0-9]+)`))
    return match?.[1] || ''
  }

  const formatDateKey = (date: Date) => formatLocalDateKey(date)

  const getDateRange = (days: number) => {
    const end = new Date()
    end.setHours(0, 0, 0, 0)
    const start = new Date(end)
    start.setDate(end.getDate() - (days - 1))
    return { startDate: formatDateKey(start), endDate: formatDateKey(end) }
  }

  /** Fetch song audience for a specific date range (Soundcharts startDate/endDate YYYY-MM-DD). */
  const fetchSongAudienceForDateRange = async (
    songUuid: string,
    platform: string,
    startDate: string,
    endDate: string,
    trackId?: string
  ) => {
    const params = new URLSearchParams({
      platform,
      startDate,
      endDate,
      ...(trackId ? { identifier: trackId } : {}),
    })
    return getDataAPI(`soundcharts/song/${songUuid}/audience?${params.toString()}`)
  }

  const fetchSongPlot = async (songUuid: string, platform: string, trackId?: string) => {
    const { startDate, endDate } = getDateRange(7)
    const query = `startDate=${startDate}&endDate=${endDate}${
      trackId ? `&identifier=${trackId}` : ''
    }`
    const endpoints = [
      `soundcharts/song/${songUuid}/plot/${platform}?${query}`,
      `soundcharts/song/${songUuid}/plot?platform=${platform}&${query}`,
    ]

    let lastError: unknown = null
    for (const endpoint of endpoints) {
      try {
        if (debugStreams) {
          console.log('[streams] fetching plot', { songUuid, platform, trackId, endpoint })
        }
        return await getDataAPI(endpoint)
      } catch (error: any) {
        // Don't log 404s as they're expected for songs without plot data
        if (error?.response?.status !== 404 && debugStreams) {
          console.log('[streams] plot fetch failed', { songUuid, platform, trackId, endpoint, error })
        }
        lastError = error
      }
    }

    // Only throw if it's not a 404
    if ((lastError as any)?.response?.status === 404) {
      return null // Silently return null for missing data
    }
    throw lastError
  }

  const fetchSoundchartsForSong = async (song: Song) => {
    const trackId =
      (song as any).spotify_id ||
      parseSpotifyId((song as any).spotify_link, 'track')
    const artistId =
      (song as any).artist_id ||
      parseSpotifyId((song as any).spotify_link, 'artist')

    try {
      let result: Partial<Song> | null = null
      let artistUuid: string | undefined

      if (trackId) {
        if (debugStreams) {
          console.log('[streams] fetching song lookup', { songId: song.id, trackId })
        }
        
        try {
          const songLookup = await getDataAPI(`soundcharts/song/platform/spotify/${trackId}`)
          if (debugStreams) {
            console.log('[streams] song lookup response', { songId: song.id, trackId, songLookup })
          }
          const songUuid = songLookup?.data?.uuid ?? (songLookup as any)?.data?.data?.uuid
          if (debugStreams) {
            console.log('[streams] song uuid', { songId: song.id, trackId, songUuid })
          }
          
          if (songUuid) {
            try {
              const audience = await getDataAPI(
                `soundcharts/song/${songUuid}/audience?platform=spotify${trackId ? `&identifier=${trackId}` : ''}`
              )
              const streams = toOptionalNumber(audience?.data?.data?.streams ?? audience?.data?.data?.stream)
              result = {
                soundcharts_data: {
                  song_uuid: songUuid,
                  streams: streams ?? 0,
                  date: audience?.data?.data?.date,
                  platform: 'spotify',
                  type: 'song_level',
                },
                ...(streams != null ? { streams_total: streams } : {}),
              }
              const audienceStreams = extractStreamCountsFromSeries(audience, trackId)
              if (audienceStreams) {
                result = {
                  ...result,
                  // Keep as a fallback only; we prefer explicit date-range pulls below.
                  ...(result?.streams_week == null && audienceStreams.streams_week != null
                    ? { streams_week: audienceStreams.streams_week }
                    : {}),
                }
                if (!result.streams_total && audienceStreams.streams_total) {
                  result = {
                    ...result,
                    streams_total: audienceStreams.streams_total,
                  }
                }
              }

              // Today's streams from Soundcharts with explicit date range (startDate/endDate = today)
              const todayKey = formatDateKey(new Date())
              try {
                const todayAudience = await fetchSongAudienceForDateRange(
                  songUuid,
                  'spotify',
                  todayKey,
                  todayKey,
                  trackId
                )
                const data = todayAudience?.data?.data ?? todayAudience?.data ?? todayAudience
                const todayFromDirect = toOptionalNumber(data?.streams ?? data?.stream)
                if (todayFromDirect !== undefined && todayFromDirect !== null) {
                  result = { ...result, streams_today: todayFromDirect }
                } else {
                  const todayFromSeries = extractStreamCountsFromSeries(todayAudience, trackId)
                  if (todayFromSeries?.streams_today != null) {
                    result = { ...result, streams_today: todayFromSeries.streams_today }
                  }
                }
              } catch (todayErr: any) {
                if (todayErr?.response?.status !== 404 && debugStreams) {
                  console.log('[streams] today audience fetch failed', { songId: song.id, todayErr })
                }
                if (audienceStreams?.streams_today != null) {
                  result = { ...result, streams_today: audienceStreams.streams_today }
                }
              }

              // This week's streams (last 7 days inclusive) from explicit date range.
              if (result?.streams_week == null) {
                const { startDate, endDate } = getDateRange(7)
                try {
                  const weekAudience = await fetchSongAudienceForDateRange(
                    songUuid,
                    'spotify',
                    startDate,
                    endDate,
                    trackId
                  )
                  const weekData = weekAudience?.data?.data ?? weekAudience?.data ?? weekAudience
                  const weekFromDirect = toOptionalNumber(weekData?.streams ?? weekData?.stream)
                  if (weekFromDirect != null) {
                    result = { ...result, streams_week: weekFromDirect }
                  } else {
                    const weekFromSeries = extractStreamCountsFromSeries(weekAudience, trackId)
                    if (weekFromSeries?.streams_week != null) {
                      result = { ...result, streams_week: weekFromSeries.streams_week }
                    }
                  }
                } catch (weekErr: any) {
                  if (weekErr?.response?.status !== 404 && debugStreams) {
                    console.log('[streams] week audience fetch failed', { songId: song.id, weekErr })
                  }
                }
              }
            } catch (audienceError: any) {
              // Silently handle 404s for missing audience data
              if (audienceError?.response?.status !== 404) {
                console.warn('Failed to fetch Soundcharts audience for song', song.id, audienceError)
              }
            }
            
            // Only fetch plot if we successfully got audience data
            if (result) {
              try {
                const plot = await fetchSongPlot(songUuid, 'spotify', trackId)
                if (debugStreams) {
                  console.log('[streams] plot response', {
                    songId: song.id,
                    songUuid,
                    trackId,
                    plot,
                  })
                }
                const plotStreams = extractStreamCountsFromSeries(plot, trackId)
                if (plotStreams) {
                  result = {
                    ...result,
                    streams_week: plotStreams.streams_week ?? result?.streams_week,
                    ...(result?.streams_today == null && plotStreams.streams_today != null
                      ? { streams_today: plotStreams.streams_today }
                      : {}),
                  }
                }
              } catch (plotError: any) {
                // Silently handle 404s for missing plot data
                if (plotError?.response?.status !== 404) {
                  console.warn('Soundcharts song plot fetch failed for song', song.id, plotError)
                }
              }
            }
          } else if (debugStreams) {
            console.log('[streams] skipping plot fetch, missing song uuid', { songId: song.id, trackId })
          }
        } catch (lookupError: any) {
          // Silently handle 404s for songs not in Soundcharts
          if (lookupError?.response?.status !== 404) {
            console.warn('Failed to lookup song in Soundcharts', song.id, lookupError)
          }
        }
      }

      if (artistId) {
        if (debugStreams) {
          console.log('[streams] fetching artist lookup', { songId: song.id, artistId })
        }
        const artistLookup = await getDataAPI(`soundcharts/artist/platform/spotify/${artistId}`)
        if (debugStreams) {
          console.log('[streams] artist lookup response', { songId: song.id, artistId, artistLookup })
        }
        artistUuid = artistLookup?.data?.uuid ?? (artistLookup as any)?.data?.data?.uuid
        if (artistUuid) {
          const audience = await getDataAPI(
            `soundcharts/artist/${artistUuid}/streaming-audience?platform=spotify`
          )
          const monthlyListeners =
            audience?.data?.data?.monthly_listeners ||
            audience?.data?.data?.listeners ||
            0
          result = {
            ...result,
            soundcharts_data: {
              ...(result?.soundcharts_data || {}),
              artist_uuid: artistUuid,
              monthly_listeners: monthlyListeners,
              date: audience?.data?.data?.date,
              platform: 'spotify',
              type: result?.soundcharts_data?.type || 'artist_level',
            },
            streams_total: (result?.streams_total || 0) || monthlyListeners || song.streams_total || 0,
          }
        }
      }

      if (artistUuid && (result?.streams_today == null || result?.streams_week == null)) {
        try {
          const localAudience = await getDataAPI(
            `soundcharts/artist/${artistUuid}/local-streaming-audience?platform=spotify`
          )
          if (debugStreams) {
            console.log('[streams] local streaming response', {
              songId: song.id,
              artistUuid,
              localAudience,
            })
          }
          const localStreams = extractStreamCountsFromSeries(localAudience)
          if (localStreams) {
            result = {
              ...result,
              streams_today: localStreams.streams_today,
              streams_week: localStreams.streams_week,
            }
          }
        } catch (error) {
          console.warn('Soundcharts local streaming fetch failed for song', song.id, error)
        }
      }

      return result
    } catch (error) {
      console.warn('Soundcharts fetch failed for song', song.id, error)
      return null
    }
  }

  const enrichWithSoundcharts = async (
    catalog: Song[],
    options?: { maxFetches?: number; force?: boolean; onlyEnrichIds?: string[] }
  ) => {
    const maxFetches = options?.maxFetches ?? 20
    const force = options?.force ?? false
    const onlyEnrichIds = options?.onlyEnrichIds
    const needsFetch = catalog.filter((song) => {
      if (onlyEnrichIds?.length) {
        const id = song.id ?? (song as any)._id
        if (!id || !onlyEnrichIds.includes(String(id))) return false
      }
      const hasSpotify =
        ((song as any).spotify_id || (song as any).spotify_link || (song as any).artist_id) ?? false
      if (!hasSpotify) return false
      if (force) return true
      const missingTotals = !song.soundcharts_data && song.streams_total == null
      const missingLocal = song.streams_today == null && song.streams_week == null
      return missingTotals || missingLocal
    })

    if (!needsFetch.length) return catalog

    const limited = Number.isFinite(maxFetches) ? needsFetch.slice(0, maxFetches) : needsFetch
    const updated = [...catalog]

    console.log(`🎵 Enriching ${limited.length} songs with Soundcharts data...`)

    // Process songs with rate limiting to avoid API spam
    for (let i = 0; i < limited.length; i++) {
      const song = limited[i]
      
      try {
        const result = await fetchSoundchartsForSong(song)
        if (result) {
          const idx = updated.findIndex((s) => s.id === song.id)
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              ...result,
            }
          }
        }
        
        // Add delay between requests to avoid rate limiting (150ms per song)
        if (i < limited.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 150))
        }
      } catch (error) {
        console.warn(`Failed to enrich song ${song.id}:`, error)
        // Continue with next song
      }
    }

    console.log(`✅ Soundcharts enrichment complete`)
    return updated
  }

  const readCachedCatalog = () => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(catalogCacheKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.songs || !Array.isArray(parsed.songs)) return null
      return parsed.songs as Song[]
    } catch (error) {
      console.warn('Failed to read catalog cache:', error)
      return null
    }
  }

  const writeCachedCatalog = (catalog: Song[]) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        catalogCacheKey,
        JSON.stringify({ timestamp: Date.now(), songs: catalog })
      )
    } catch (error) {
      console.warn('Failed to write catalog cache:', error)
    }
  }

  useEffect(() => {
    const cached = readCachedCatalog()
    
    // If we have cache, show it immediately
    if (cached && cached.length) {
      setSongs(cached)
      setIsLoading(false)
    }
    
    // Fetch fresh data from backend (without auto-enrichment to keep it fast)
    const fetchSongs = async () => {
      try {
        const data = await getDataAPI('user/getCatalog')
        const normalized = normalizeCatalog(data)
        
        // Merge with cached data to preserve any enriched streaming data
        const merged = normalized.map((freshSong) => {
          const cachedSong = cached?.find((s) => s.id === freshSong.id)
          if (cachedSong) {
            return {
              ...freshSong,
              // Preserve enriched streaming data from cache
              soundcharts_data: freshSong.soundcharts_data || cachedSong.soundcharts_data,
              streams_total: freshSong.streams_total ?? cachedSong.streams_total,
              streams_today: freshSong.streams_today ?? cachedSong.streams_today,
              streams_week: freshSong.streams_week ?? cachedSong.streams_week,
              // Preserve other enriched data
              suggested_writers: freshSong.suggested_writers || cachedSong.suggested_writers,
              master_contributors: freshSong.master_contributors || cachedSong.master_contributors,
              publishing_contributors: freshSong.publishing_contributors || cachedSong.publishing_contributors,
            }
          }
          return freshSong
        })
        
        setSongs(merged)
      } catch (error) {
        console.error('Error fetching songs:', error)
        // If fetch fails and we have cache, keep using it
      } finally {
        setIsLoading(false)
      }
    }
    
    // Fetch fresh data in background to stay in sync
    fetchSongs()
  }, [])

  useEffect(() => {
    if (isLoading) return
    if (!songs.length) return
    writeCachedCatalog(songs)
  }, [songs, isLoading])

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  // Check for edit item from navigation state
  useEffect(() => {
    const state = location.state as any
    if (state?.editItemId && state?.source === 'music' && songs.length > 0) {
      const song = songs.find((s) => s.id === state.editItemId)
      if (song) {
        startEditSong(song)
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, songs])

  const refresh = async (onlyEnrichIds?: string[]) => {
    const data = await getDataAPI('user/getCatalog').catch(() => [])
    const normalized = normalizeCatalog(data)
    
    // Preserve existing enriched data from songs already in state
    const merged = normalized.map((freshSong) => {
      const existingSong = songs.find((s) => s.id === freshSong.id)
      if (existingSong) {
        return {
          ...freshSong,
          // Preserve enriched streaming data
          soundcharts_data: freshSong.soundcharts_data || existingSong.soundcharts_data,
          streams_total: freshSong.streams_total ?? existingSong.streams_total,
          streams_today: freshSong.streams_today ?? existingSong.streams_today,
          streams_week: freshSong.streams_week ?? existingSong.streams_week,
          // Preserve ISRC code if not in fresh data
          isrc: freshSong.isrc || existingSong.isrc,
          // Preserve songwriter/credits data if not in fresh data
          suggested_writers: freshSong.suggested_writers || existingSong.suggested_writers,
          master_contributors: freshSong.master_contributors || existingSong.master_contributors,
          publishing_contributors: freshSong.publishing_contributors || existingSong.publishing_contributors,
          // Preserve any other enriched metadata
          artwork: freshSong.artwork || existingSong.artwork,
          spotify_id: freshSong.spotify_id || existingSong.spotify_id,
          artist_id: freshSong.artist_id || existingSong.artist_id,
        }
      }
      return freshSong
    })
    
    const enriched = await enrichWithSoundcharts(merged, onlyEnrichIds?.length ? { onlyEnrichIds, maxFetches: onlyEnrichIds.length } : undefined)
    setSongs(enriched)
  }

  const refreshStreaming = async () => {
    setIsRefreshingStreams(true)
    try {
      const catalogIds = songs
        .filter(
          (song) =>
            song.id &&
            (song.spotify_id || song.spotify_link || (song as any).artist_id)
        )
        .map((song) => song.id)

      if (catalogIds.length === 0) {
        alert('No songs with Spotify links found to refresh.')
        return
      }

      // Legacy flow: re-import catalog items from Spotify (includes Soundcharts)
      try {
        await postDataAPI('catalog/refresh', { catalogIds })
        await refresh()
        return
      } catch (error) {
        console.warn('Legacy catalog refresh failed, falling back to Soundcharts:', error)
      }

      const enriched = await enrichWithSoundcharts(songs, { maxFetches: 200, force: true })
      setSongs(enriched)
    } catch (error) {
      console.error('Failed to refresh streaming data:', error)
      alert('Failed to refresh streaming data. Please try again.')
    } finally {
      setIsRefreshingStreams(false)
    }
  }

  const resetAddModal = () => {
    setAddView('options')
    setSingleQuery('')
    setSingleResults([])
    setSingleSelected(null)
    setSingleImporting(false)
    setBulkArtistName('')
    setBulkSpotifyUrl('')
    setBulkArtistResults([])
    setBulkArtistSearching(false)
    setBulkArtistSelected(null)
    setBulkResults([])
    setBulkLoading(false)
    setBulkImporting(false)
    setBulkProgress({ current: 0, total: 0, currentBatch: 0, totalBatches: 0 })
    setBulkPhase(null)
    setIsEditing(false)
    setEditCatalogId(null)
    setCreditsSongId(null)
    setSuggestedWritersForEdit([])
  }

  const goBackToOptions = () => {
    setAddView('options')
    setCreditsSongId(null)
    setOpenDropdown(null)
  }

  const mapSpotifyTrackToSong = (track: any) => {
    const primaryArtist = track?.artists?.[0]?.name || ''
    const featuredArtists = (track?.artists || []).slice(1).map((a: any) => a?.name).filter(Boolean)
    return {
      song_title: track?.name || '',
      status: 'released',
      release_date: track?.album?.release_date || '',
      duration: track?.duration_ms ? Math.round(track.duration_ms / 1000) : '',
      isrc: track?.external_ids?.isrc || '',
      artwork: track?.album?.images?.[0]?.url || '',
      album_artwork: track?.album?.images?.[0]?.url || '',
      spotify_link: track?.external_urls?.spotify || '',
      primary_artist: primaryArtist,
      artist_name: primaryArtist,
      featured_artists: featuredArtists.join(', '),
      primary_artists: primaryArtist ? [primaryArtist] : [],
      album_name: track?.album?.name || '',
    }
  }

  const mapDiscoveredTrackToSong = (track: any) => {
    const primaryArtist =
      track?.artist_name || track?.artist || track?.primary_artist || track?.primaryArtist || ''
    const featuredArtists = Array.isArray(track?.featured_artists)
      ? track.featured_artists
      : typeof track?.featured_artists === 'string'
        ? track.featured_artists.split(',').map((a: string) => a.trim()).filter(Boolean)
        : []
    const durationSeconds = typeof track?.duration === 'number'
      ? track.duration
      : track?.duration_ms
        ? Math.round(track.duration_ms / 1000)
        : ''
    const spotifyId =
      track?.spotify_track_id ||
      track?.spotify_id ||
      track?.spotifyId ||
      track?.spotify_trackid ||
      ''
    const spotifyLink =
      track?.spotify_url ||
      track?.spotifyUrl ||
      track?.spotify_link ||
      track?.spotifyLink ||
      (spotifyId ? `https://open.spotify.com/track/${spotifyId}` : '')
    return {
      song_title: track?.song_title || track?.title || track?.name || '',
      status: track?.status || 'released',
      release_date: track?.release_date || track?.releaseDate || '',
      duration: durationSeconds,
      isrc: track?.isrc || track?.ISRC || '',
      artwork: track?.album_artwork || track?.albumArtwork || track?.artwork || '',
      album_artwork: track?.album_artwork || track?.albumArtwork || track?.artwork || '',
      spotify_link: spotifyLink,
      spotify_id: spotifyId || undefined,
      primary_artist: primaryArtist,
      artist_name: primaryArtist,
      featured_artists: featuredArtists.join(', '),
      primary_artists: primaryArtist ? [primaryArtist] : [],
      album_name: track?.album_name || track?.album || '',
      record_label: track?.record_label || track?.label || '',
      label: track?.record_label || track?.label || '',
      distributor: track?.distributor || '',
      genre: track?.genre || '',
    }
  }

  const handleSingleSearch = async (query: string) => {
    if (!query.trim()) {
      setSingleResults([])
      return
    }
    try {
      const response = await postDataAPI('user/searchTracksForImport', { query: query.trim() })
      const tracks =
        response?.data?.data?.tracks?.items ||
        response?.data?.tracks?.items ||
        response?.data?.data ||
        response?.data ||
        []
      setSingleResults(Array.isArray(tracks) ? tracks : [])
    } catch (error) {
      console.error('Failed to search tracks:', error)
      alert('Failed to search tracks. Please try again.')
    }
  }

  useEffect(() => {
    if (addView !== 'single') return
    const trimmed = singleQuery.trim()
    if (!trimmed) {
      setSingleResults([])
      return
    }
    const timer = window.setTimeout(() => {
      handleSingleSearch(trimmed)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [addView, singleQuery])

  const handleBulkArtistSearch = async (query: string) => {
    if (!query.trim()) {
      setBulkArtistResults([])
      return
    }
    setBulkArtistSearching(true)
    try {
      const response = await postDataAPI('user/searchArtistsForBulkImport', {
        artistName: query.trim(),
      })
      const artists =
        response?.data?.response ||
        response?.data?.data?.response ||
        response?.data?.data ||
        response?.data ||
        []
      setBulkArtistResults(Array.isArray(artists) ? artists : [])
    } catch (error) {
      console.error('Failed to search artists for bulk import:', error)
      alert('Failed to search artists. Please try again.')
    } finally {
      setBulkArtistSearching(false)
    }
  }

  useEffect(() => {
    if (addView !== 'bulk') return
    const trimmed = bulkArtistName.trim()
    if (!trimmed) {
      setBulkArtistResults([])
      return
    }
    if (bulkArtistSelected && bulkArtistSelected.name === trimmed) return
    const timer = window.setTimeout(() => {
      handleBulkArtistSearch(trimmed)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [addView, bulkArtistName, bulkArtistSelected])

  const handleSingleImport = async () => {
    if (!singleSelected) return
    setSingleImporting(true)
    try {
      const payload = mapSpotifyTrackToSong(singleSelected)
      const response = await postDataAPI('user/saveCatalog', { data: [payload] })
      const saved = response?.data?.data ?? response?.data
      const newIds = Array.isArray(saved) ? saved.map((item: any) => item?._id ?? item?.id).filter(Boolean) : []
      await refresh(newIds.length ? newIds : undefined)
      setIsAddOpen(false)
      resetAddModal()
    } catch (error) {
      console.error('Failed to import track:', error)
      alert('Failed to import track. Please try again.')
    } finally {
      setSingleImporting(false)
    }
  }

  const handleBulkDiscover = async () => {
    if (!bulkArtistName.trim() && !bulkSpotifyUrl.trim()) return
    setBulkLoading(true)
    
    console.log('🚀 Starting bulk catalog discovery with polling...')
    
    // Start discovery progress UI
    setDiscoveryProgress({
      percentage: 0,
      isActive: true,
      message: 'Starting discovery...'
    })
    
    try {
      const requestData = {
        artistName: bulkArtistName.trim() || undefined,
        spotifyUrl: bulkSpotifyUrl.trim() || undefined,
      }
      
      console.log('📝 Starting discovery job...')
      
      // Start the discovery job
      const startResponse = await postDataAPI('user/discoverCatalogFromArtist/start', requestData, undefined, {
        timeout: 30000, // Only 30s for starting the job
      })
      
      const jobId = startResponse?.data?.jobId
      if (!jobId) {
        throw new Error('Failed to start discovery job')
      }
      
      console.log(`✅ Discovery job started: ${jobId}`)
      
      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const token = localStorage.getItem('dizeeToken')
          const parsedToken = token ? JSON.parse(token) : null
          
          const statusResponse = await fetch(
            `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'}/api/v1/user/discoverCatalogFromArtist/status/${jobId}`,
            {
              headers: {
                'Authorization': `Bearer ${parsedToken}`,
                'Content-Type': 'application/json'
              }
            }
          )
          
          if (!statusResponse.ok) {
            throw new Error('Failed to check job status')
          }
          
          const statusData = await statusResponse.json()
          const job = statusData.data
          
          console.log(`📊 Job ${jobId} status:`, job.status, job.progress)
          
          // Update progress UI
          setDiscoveryProgress({
            percentage: job.progress.percentage,
            isActive: job.status === 'processing',
            message: job.progress.message
          })
          
          // Check if completed
          if (job.status === 'completed') {
            clearInterval(pollInterval)
            
            console.log('✅ Discovery completed!', job.result)
            
            const tracks = job.result?.data || []
            setBulkResults(Array.isArray(tracks) ? tracks : [])
            
            setDiscoveryProgress({
              percentage: 100,
              isActive: false,
              message: `Discovery completed! Found ${tracks.length} tracks`
            })
            
            setBulkLoading(false)
          } else if (job.status === 'failed') {
            clearInterval(pollInterval)
            throw new Error(job.error || 'Discovery failed')
          }
          
        } catch (pollError: any) {
          console.error('Error polling job status:', pollError)
          clearInterval(pollInterval)
          throw pollError
        }
      }, 2000) // Poll every 2 seconds
      
      // Cleanup polling after 15 minutes max
      setTimeout(() => {
        clearInterval(pollInterval)
      }, 900000)
      
    } catch (error: any) {
      console.error('💥 Discovery failed:', error)
      
      setDiscoveryProgress({
        percentage: 0,
        isActive: false,
        message: 'Discovery failed'
      })
      
      setBulkLoading(false)
      
      alert(
        `Failed to discover catalog.\n\n` +
        `Error: ${error.message}\n\n` +
        `Please try again or contact support if the issue persists.`
      )
    }
  }

  const handleBulkImportAll = async () => {
    if (!bulkResults.length) return
    setBulkImporting(true)
    setBulkPhase('saving')
    setBulkProgress({ current: 0, total: bulkResults.length, currentBatch: 0, totalBatches: 0 })
    try {
      const payloads = bulkResults
        .map(mapDiscoveredTrackToSong)
        .filter((payload) => payload.song_title && payload.primary_artist)

      if (payloads.length === 0) {
        alert('No valid tracks found to import.')
        setBulkPhase(null)
        return
      }

      let newIds: string[] = []
      const total = payloads.length

      if (payloads.length > 20) {
        const batchSize = 20
        const totalBatches = Math.ceil(payloads.length / batchSize)
        setBulkProgress({ current: 0, total, currentBatch: 0, totalBatches })

        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
          const start = batchIndex * batchSize
          const batch = payloads.slice(start, start + batchSize)

          const response = await postDataAPI('user/saveCatalog', {
            data: batch,
          })

          const saved = response?.data?.data ?? response?.data
          const batchIds = Array.isArray(saved) ? saved.map((item: any) => item?._id ?? item?.id).filter(Boolean) : []
          newIds.push(...batchIds)

          const completed = Math.min(start + batch.length, total)
          setBulkProgress({
            current: completed,
            total,
            currentBatch: batchIndex + 1,
            totalBatches,
          })

          if (batchIndex < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }
        }
      } else {
        const response = await postDataAPI('user/saveCatalog', { data: payloads })
        const saved = response?.data?.data ?? response?.data
        newIds = Array.isArray(saved) ? saved.map((item: any) => item?._id ?? item?.id).filter(Boolean) : []
        setBulkProgress((p) => ({ ...p, current: total, total }))
      }

      setBulkPhase('updating')
      
      // Ensure newly-saved songs get fully re-imported/enriched server-side (Spotify/Soundcharts),
      // which helps populate fields like ISRC that credits lookup depends on.
      if (newIds.length > 0) {
        try {
          await postDataAPI('catalog/refresh', { catalogIds: newIds })
        } catch (error) {
          // Non-fatal: we'll still refresh and attempt credits with whatever data we have.
          console.warn('Bulk import: catalog refresh failed, continuing without it:', error)
        }
      }

      // Refresh UI state + enrich streams for imported songs.
      await refresh(newIds.length ? newIds : undefined)

      if (newIds.length > 0) {
        // Poll briefly for ISRCs to land (server-side refresh can be async/eventually consistent).
        const maxWaitMs = 120_000
        const intervalMs = 2_000
        const start = Date.now()

        let normalizedCatalog: Song[] = []
        while (Date.now() - start < maxWaitMs) {
          const catalogAfterRefresh = await getDataAPI('user/getCatalog').catch(() => [])
          normalizedCatalog = normalizeCatalog(catalogAfterRefresh)
          const newSongs = normalizedCatalog.filter((s: Song) => newIds.includes(s.id))
          const withIsrc = newSongs.filter((s) => Boolean(s.isrc))
          if (withIsrc.length === newSongs.length && newSongs.length > 0) break
          await sleep(intervalMs)
        }

        const newSongsWithIsrc = normalizedCatalog.filter((s: Song) => newIds.includes(s.id) && s.isrc)
        if (newSongsWithIsrc.length > 0) {
          // Fetch credits sequentially to avoid rate limiting / request spikes.
          for (let i = 0; i < newSongsWithIsrc.length; i += 1) {
            // Retry a couple times for transient issues.
            let ok = await fetchCreditsForSong(newSongsWithIsrc[i], true)
            if (!ok) {
              await sleep(350)
              ok = await fetchCreditsForSong(newSongsWithIsrc[i], true)
            }
            if (i < newSongsWithIsrc.length - 1) {
              await sleep(200)
            }
          }
          await refresh()
        }
      }

      setBulkPhase('done')
      setBulkProgress((p) => ({ ...p, current: total, total }))
      setIsAddOpen(false)
      resetAddModal()
    } catch (error) {
      console.error('Failed to import catalog:', error)
      alert('Failed to import catalog. Please try again.')
      setBulkPhase(null)
    } finally {
      setBulkImporting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      if (!String(editData.song_title || '').trim()) {
        alert('Song title is required')
        return
      }
      const validPrimaryArtists = (editData.primary_artists || []).filter((a: string) => a && a.trim())
      if (validPrimaryArtists.length === 0) {
        alert('At least one primary artist is required')
        return
      }

      const spotifyLink = String(editData.spotify_link || '').trim()
      const spotifyTrackId = parseSpotifyId(spotifyLink, 'track')
      const spotifyArtistId = parseSpotifyId(spotifyLink, 'artist')

      const finalData = {
        ...editData,
        song_title: String(editData.song_title || '').trim(),
        primary_artist: validPrimaryArtists.join(', '),
        artist_name: validPrimaryArtists.join(', '),
        spotify_link: spotifyLink || editData.spotify_link,
        spotify_id: spotifyTrackId || (editData as any).spotify_id,
        artist_id: spotifyArtistId || (editData as any).artist_id,
        featured_artists: (editData.featured_artists || []).filter((a: string) => a && a.trim()).join(', '),
        master_contributors: masterContributors
          .filter((c) => c.name && c.name.trim())
          .map((c) => ({
            name: c.name.trim(),
            role: c.role,
            percentage: parseFloat(c.percentage) || 0,
            advance_fee: parseFloat(c.advanceFee) || 0,
          })),
        publishing_contributors: publishingContributors
          .filter((c) => c.name && c.name.trim())
          .map((c) => ({
            name: c.name.trim(),
            role: c.role,
            percentage: parseFloat(c.percentage) || 0,
            pro: c.pro,
            ipi_number: c.ipiNumber,
            publishing_line: c.publishingLine,
          })),
      }

      if (isEditing && editCatalogId) {
        await putDataAPI('user/updateCatalog', {
          catalogId: editCatalogId,
          data: finalData,
        })

        // If this song had suggested writers, persist review state so "Unconfirmed" doesn't linger
        // after adding them to Publishing Contributors (or removing them).
        const existingSong = songs.find((s) => s.id === editCatalogId)
        const hadSuggestedWriters =
          (existingSong?.suggested_writers?.length ?? 0) > 0 || suggestedWritersForEdit.length > 0
        if (hadSuggestedWriters) {
          const publishingNameSet = new Set(
            (finalData.publishing_contributors || [])
              .map((c: any) => normalizeCreditName(c?.name))
              .filter(Boolean)
          )
          const remainingSuggested = (suggestedWritersForEdit || [])
            .filter((w) => w?.name && !publishingNameSet.has(normalizeCreditName(w.name)))
            .map((w) => ({
              name: w.name,
              role: w.role || 'Writer',
              source: w.source || 'soundcharts',
            }))

          await postDataAPI(`user/catalog/${editCatalogId}/suggested-writers`, {
            suggested_writers: remainingSuggested,
            split_status: remainingSuggested.length > 0 ? 'unconfirmed' : 'confirmed',
          })
        }
      } else {
        await postDataAPI('user/saveCatalog', { data: [finalData] })
      }
      setIsAddOpen(false)
      resetAddModal()
      await refresh()
    } catch (error) {
      console.error('Failed to create song:', error)
      alert('Failed to create song. Please check fields and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStatusChange = async (songId: string, newStatus: 'demo' | 'unreleased' | 'released') => {
    try {
      await putDataAPI('user/updateCatalog', {
        catalogId: songId,
        data: { status: newStatus },
      })
      setSongs((prev) =>
        prev.map((song) => (song.id === songId ? { ...song, status: newStatus } : song))
      )
      setOpenDropdown(null)
    } catch (error) {
      console.error('Failed to update status:', error)
      alert('Failed to update status. Please try again.')
    }
  }

  const startEditSong = (song: Song) => {
    setIsEditing(true)
    setEditCatalogId(song.id)
    setAddView('manual')
    setIsAddOpen(true)
    setEditData((prev) => ({
      ...prev,
      song_title: song.song_title || song.title || '',
      status: song.status || 'unreleased',
      release_date: song.release_date || '',
      duration: (song as any).duration || '',
      bpm: (song as any).bpm || '',
      key: (song as any).key || '',
      genre: (song as any).genre || '',
      track_type: (song as any).track_type || '',
      language: (song as any).language || '',
      explicit: (song as any).explicit || 'No',
      isrc: (song as any).isrc || '',
      artwork: (song as any).artwork || (song as any).album_artwork || '',
      audio_file: (song as any).audio_file || '',
      label: (song as any).label || (song as any).record_label || '',
      distributor: (song as any).distributor || '',
      spotify_link: (song as any).spotify_link || '',
      apple_music_link: (song as any).apple_music_link || '',
      youtube_link: (song as any).youtube_link || '',
      sound_cloud_link: (song as any).sound_cloud_link || '',
      internal_notes: (song as any).internal_notes || '',
      primary_artists: (song.primary_artist || song.artist || '')
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
      featured_artists: (song.featured_artists || '')
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
    }))

    if (song.master_contributors && song.master_contributors.length > 0) {
      setMasterContributors(
        song.master_contributors.map((c) => ({
          name: c.name || '',
          role: c.role || 'Writer',
          percentage: c.percentage ? String(c.percentage) : '',
          advanceFee: c.advance_fee ? String(c.advance_fee) : '',
        }))
      )
    } else {
      setMasterContributors([{ name: '', role: 'Writer', percentage: '', advanceFee: '' }])
    }

    if (song.publishing_contributors && song.publishing_contributors.length > 0) {
      setPublishingContributors(
        song.publishing_contributors.map((c) => ({
          name: c.name || '',
          role: c.role || 'Writer',
          percentage: c.percentage ? String(c.percentage) : '',
          pro: c.pro || '',
          ipiNumber: c.ipi_number || '',
          publishingLine: c.publishing_line || '',
        }))
      )
    } else {
      setPublishingContributors([{ name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }])
    }

    // Load suggested writers if available
    if (song.suggested_writers && song.suggested_writers.length > 0) {
      setSuggestedWritersForEdit(song.suggested_writers.map(w => ({ 
        name: w.name, 
        role: w.role || 'Writer', 
        source: w.source 
      })))
    } else {
      setSuggestedWritersForEdit([])
    }
  }

  const handleDeleteSong = async (songId: string) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return
    try {
      await deleteDataAPI(`user/deleteCatalog/${songId}`)
      setSongs((prev) => prev.filter((song) => song.id !== songId))
      setSelectedSongIds((prev) => {
        const next = new Set(prev)
        next.delete(songId)
        return next
      })
      setOpenDropdown(null)
    } catch (error: any) {
      console.error('Failed to delete song:', error)
      
      const statusCode = error?.response?.status
      
      // If 404, the song is already deleted - remove it from frontend state
      if (statusCode === 404) {
        console.log('Song already deleted on backend, removing from frontend state')
        setSongs((prev) => prev.filter((song) => song.id !== songId))
        setSelectedSongIds((prev) => {
          const next = new Set(prev)
          next.delete(songId)
          return next
        })
        setOpenDropdown(null)
        // Refresh to ensure we're in sync with backend
        setTimeout(() => refresh(), 500)
        return
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete song'
      
      if (statusCode === 401 || statusCode === 403) {
        alert('Authentication error. Please try logging out and logging back in.')
      } else if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        alert('Network error. Please check that your backend server is running.')
      } else {
        alert(`Failed to delete song: ${errorMessage}`)
      }
    }
  }

  const toggleSongSelection = (songId: string) => {
    setSelectedSongIds((prev) => {
      const next = new Set(prev)
      if (next.has(songId)) next.delete(songId)
      else next.add(songId)
      return next
    })
  }

  const selectAllFiltered = () => {
    const ids = filteredSongs.map((s) => s.id)
    if (ids.length === 0) return
    setSelectedSongIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        ids.forEach((id) => next.delete(id))
        return next
      }
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  const handleBulkDelete = async () => {
    if (selectedSongIds.size === 0) return
    if (!window.confirm(`Delete ${selectedSongIds.size} selected song${selectedSongIds.size === 1 ? '' : 's'}? This cannot be undone.`)) return
    setIsBulkDeleting(true)
    
    const idsToDelete = Array.from(selectedSongIds)
    let successCount = 0
    let notFoundCount = 0
    let errorCount = 0
    
    try {
      // Delete each song individually to track which ones succeed/fail
      const results = await Promise.allSettled(
        idsToDelete.map((id) => deleteDataAPI(`user/deleteCatalog/${id}`))
      )
      
      results.forEach((result, _index) => {
        if (result.status === 'fulfilled') {
          successCount++
        } else {
          const error: any = result.reason
          const statusCode = error?.response?.status
          if (statusCode === 404) {
            notFoundCount++
          } else {
            errorCount++
          }
        }
      })
      
      // Remove all selected songs from frontend (including already-deleted ones)
      // This preserves enriched streaming data for remaining songs
      setSongs((prev) => prev.filter((s) => !selectedSongIds.has(s.id)))
      setSelectedSongIds(new Set())
      setOpenDropdown(null)
      
      // Show summary of what happened
      if (notFoundCount > 0) {
        console.log(`${notFoundCount} song(s) were already deleted.`)
      }
      
      if (errorCount > 0) {
        alert(`Deleted ${successCount} song(s). ${errorCount} failed. ${notFoundCount} were already deleted.`)
      }
      
    } catch (error: any) {
      console.error('Failed to delete songs:', error)
      alert('An unexpected error occurred during bulk delete.')
    } finally {
      setIsBulkDeleting(false)
    }
  }

  // Fetch credits for a single song (silent mode for bulk operations)
  const fetchCreditsForSong = async (song: Song, silent = false): Promise<boolean> => {
    if (!song.isrc || !song.id) return false

    try {
      const response = await getDataAPI(`user/credits/isrc/${song.isrc}`)
      const payload = response?.data ?? response
      const credits = payload?.data?.credits ?? payload?.credits

      if (credits && Array.isArray(credits) && credits.length > 0) {
        const suggestedWriters = credits.map((w: { name?: string; role?: string; source?: string }) => ({
          name: w?.name ?? 'Unknown Writer',
          role: w?.role ?? 'Writer',
          source: w?.source ?? 'soundcharts'
        }))

        await postDataAPI(`user/catalog/${song.id}/suggested-writers`, {
          suggested_writers: suggestedWriters,
          split_status: 'unconfirmed'
        })
        return true
      }
      return false
    } catch (error) {
      if (!silent) {
        console.warn(`Failed to fetch credits for song ${song.id}:`, error)
      }
      return false
    }
  }

  // Fetch credits for multiple songs (used after bulk import)
  /* Kept for future use
  const fetchCreditsForSongs = async (songIds: string[], onProgress?: (current: number, total: number) => void) => {
    // Get the songs from state that match the IDs and have ISRCs
    const songsWithIsrc = songs.filter((s) => songIds.includes(s.id) && s.isrc)
    if (songsWithIsrc.length === 0) return 0

    let successCount = 0
    for (let i = 0; i < songsWithIsrc.length; i++) {
      const song = songsWithIsrc[i]
      onProgress?.(i + 1, songsWithIsrc.length)
      
      const success = await fetchCreditsForSong(song, true)
      if (success) successCount++
      
      // Small delay between requests to avoid rate limiting
      if (i < songsWithIsrc.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200))
      }
    }
    return successCount
  }
  */

  const handleFetchCredits = async (song: Song) => {
    if (!song.isrc) {
      alert('This song does not have an ISRC code. Credits can only be fetched for songs with an ISRC.')
      return
    }

    setFetchingMusoCredits(song.id)
    try {
      const success = await fetchCreditsForSong(song, false)
      if (success) {
        await refresh()
        alert('Found and saved suggested writer(s) for this song.')
      } else {
        alert('This song was found but no writer credits are available in our database.')
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } }
      console.error('Failed to fetch credits:', error)
      if (err?.response?.status === 503) {
        alert('Credits API is not configured. Please contact your administrator.')
      } else if (err?.response?.status === 404) {
        alert('No song found for this ISRC.')
      } else {
        alert('Failed to fetch credits. Please try again.')
      }
    } finally {
      setFetchingMusoCredits(null)
    }
  }

  const streamsTotal = songs.reduce((sum, s) => sum + (s.streams_manual_override ?? s.streams_total ?? 0), 0)
  const streamsToday = songs.reduce((sum, s) => sum + (s.streams_today || 0), 0)
  const streamsWeek = songs.reduce((sum, s) => sum + (s.streams_week || 0), 0)

  const searchLower = searchQuery.trim().toLowerCase()
  const filteredSongs = songs
    .filter((song) => {
      if (statusFilter && (song.status || '') !== statusFilter) return false
      if (!searchLower) return true
      const title = (song.song_title || song.title || '').toLowerCase()
      const artist = (song.primary_artist || song.artist || '').toLowerCase()
      const isrc = (song.isrc || '').toLowerCase()
      return (
        title.includes(searchLower) ||
        artist.includes(searchLower) ||
        isrc.includes(searchLower)
      )
    })
    .sort((a, b) => {
      const dateA = a.release_date ? new Date(a.release_date).getTime() : Number.MAX_SAFE_INTEGER
      const dateB = b.release_date ? new Date(b.release_date).getTime() : Number.MAX_SAFE_INTEGER
      return dateA - dateB
    })
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const upcomingSongs = songs
    .filter(s => s.status === 'unreleased' && s.release_date)
    .map(s => ({
      ...s,
      releaseDate: new Date(s.release_date!),
    }))
    .filter(s => s.releaseDate >= today)
    .sort((a, b) => a.releaseDate.getTime() - b.releaseDate.getTime())
  
  const nextUpSong = upcomingSongs[0] || {
    song_title: 'New Single',
    title: 'New Single',
    primary_artist: 'Artist Name',
    artist: 'Artist Name',
    release_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }
  
  const getDaysUntilRelease = (releaseDate: Date | string) => {
    const date = typeof releaseDate === 'string' ? new Date(releaseDate) : releaseDate
    const diff = date.getTime() - today.getTime()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }
  
  const nextUpReleaseDate = typeof nextUpSong.release_date === 'string' 
    ? new Date(nextUpSong.release_date) 
    : (nextUpSong as any).releaseDate || new Date(nextUpSong.release_date!)

  const modalMeta = {
    options: {
      title: 'Add Song',
      description: 'Choose how you want to add music.',
    },
    manual: {
      title: 'Manual Add',
      description: 'Fill in required fields.',
    },
    single: {
      title: 'Single Import',
      description: 'Search Spotify and import one track.',
    },
    bulk: {
      title: 'Bulk Import',
      description: 'Discover and import a full catalog.',
    },
    credits: {
      title: 'Song Credits',
      description: 'Review credits by song.',
    },
  } as const

  const creditsSongs = creditsSongId ? songs.filter((song) => song.id === creditsSongId) : songs

  return (
    <div className="p-8 pb-24 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Music</h1>
          <p className="text-sm text-gray-500">Canonical catalog of all music</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            disabled={isRefreshingStreams}
            onClick={refreshStreaming}
          >
            {isRefreshingStreams ? 'Refreshing…' : 'Refresh Streams'}
          </Button>
          <Button
            onClick={() => {
              setAddView('options')
            setIsEditing(false)
            setEditCatalogId(null)
              setIsAddOpen(true)
            }}
          >
            Add Song
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false)
          resetAddModal()
        }}
        title={modalMeta[addView].title}
        description={modalMeta[addView].description}
        maxWidthClassName="max-w-4xl"
      >
        {addView === 'options' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'single', title: 'Single Import', description: 'Search Spotify and import one track.' },
              { key: 'bulk', title: 'Bulk Import', description: 'Discover and import a full catalog.' },
              { key: 'manual', title: 'Manual Add', description: 'Add a song with full metadata and credits.' },
              { key: 'credits', title: 'Song Credits', description: 'Review credits by song.' },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className="text-left rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-colors"
                onClick={() => setAddView(option.key as any)}
              >
                <div className="text-sm font-medium text-gray-900">{option.title}</div>
                <div className="text-xs text-gray-500 mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        ) : addView === 'manual' ? (
          <form onSubmit={handleCreate} className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Required details</div>
              <button
                type="button"
                onClick={goBackToOptions}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              {requiredFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-2">
                    {field.label}
                    {(field as any).required ? <span className="text-red-500 ml-1">*</span> : null}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              {optionalFields.map((field) => (
                <div key={field.key} className={'type' in field && field.type === 'textarea' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium mb-2">{field.label}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>

            {/* Primary Artists */}
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">
                  Primary Artists <span className="text-red-500">*</span>
                </label>
                {(editData.primary_artists || []).length < 6 && (
                  <button
                    type="button"
                    onClick={() =>
                      setEditData((prev) => ({ ...prev, primary_artists: [...(prev.primary_artists || []), ''] }))
                    }
                    className="text-xs text-gray-600 hover:text-gray-900"
                  >
                    + Add
                  </button>
                )}
              </div>
              {(editData.primary_artists || []).map((artist: string, idx: number) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={artist}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        primary_artists: (prev.primary_artists || []).map((a: string, i: number) =>
                          i === idx ? e.target.value : a
                        ),
                      }))
                    }
                    placeholder={`Primary artist ${idx + 1}`}
                    className="ds-input flex-1"
                  />
                  {(editData.primary_artists || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEditData((prev) => ({
                          ...prev,
                          primary_artists: (prev.primary_artists || []).filter((_: any, i: number) => i !== idx),
                        }))
                      }
                      className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Featured Artists */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Featured Artists</label>
                {(editData.featured_artists || []).length < 6 && (
                  <button
                    type="button"
                    onClick={() =>
                      setEditData((prev) => ({ ...prev, featured_artists: [...(prev.featured_artists || []), ''] }))
                    }
                    className="text-xs text-gray-600 hover:text-gray-900"
                  >
                    + Add
                  </button>
                )}
              </div>
              {(editData.featured_artists || []).map((artist: string, idx: number) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={artist}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        featured_artists: (prev.featured_artists || []).map((a: string, i: number) =>
                          i === idx ? e.target.value : a
                        ),
                      }))
                    }
                    placeholder={`Featured artist ${idx + 1}`}
                    className="ds-input flex-1"
                  />
                  {(editData.featured_artists || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEditData((prev) => ({
                          ...prev,
                          featured_artists: (prev.featured_artists || []).filter((_: any, i: number) => i !== idx),
                        }))
                      }
                      className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Master Contributors */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Master Contributors</label>
                <button
                  type="button"
                  onClick={() => setMasterContributors((prev) => [...prev, { name: '', role: 'Writer', percentage: '', advanceFee: '' }])}
                  className="text-xs text-gray-600 hover:text-gray-900"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {masterContributors.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input
                      className="ds-input col-span-4"
                      placeholder="Name"
                      value={c.name}
                      onChange={(e) => setMasterContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-3"
                      placeholder="Role"
                      value={c.role}
                      onChange={(e) => setMasterContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, role: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-2"
                      placeholder="%"
                      value={c.percentage}
                      onChange={(e) => setMasterContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, percentage: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-2"
                      placeholder="Advance"
                      value={c.advanceFee}
                      onChange={(e) => setMasterContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, advanceFee: e.target.value } : x)))}
                    />
                    <button
                      type="button"
                      className="col-span-1 text-red-600 hover:text-red-800"
                      onClick={() => setMasterContributors((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Suggested Writers – only show those not already in Publishing Contributors */}
            {(() => {
              const existingNames = new Set(
                publishingContributors.map((c) => c.name.toLowerCase().trim()).filter(Boolean)
              )
              const toShow = suggestedWritersForEdit.filter(
                (w) => w.name && !existingNames.has(w.name.toLowerCase().trim())
              )
              if (toShow.length === 0) return null
              return (
              <div className="space-y-3 pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">Suggested Writers</label>
                    <p className="text-xs text-gray-500 mt-1">
                      These songwriter credits were automatically pulled. Please review and confirm.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      const existingNames = new Set(
                        publishingContributors.map((c) => c.name.toLowerCase().trim()).filter(Boolean)
                      )
                      const newContributors = toShow
                        .filter((w) => w.name && !existingNames.has(w.name.toLowerCase().trim()))
                        .map((w) => ({
                          name: w.name,
                          role: w.role || 'Writer',
                          percentage: '',
                          pro: '',
                          ipiNumber: '',
                          publishingLine: '',
                        }))
                      setPublishingContributors((prev) => [...prev, ...newContributors])
                      const addedNames = new Set(newContributors.map((c) => c.name.toLowerCase().trim()))
                      setSuggestedWritersForEdit((prev) => prev.filter((w) => !addedNames.has((w.name || '').toLowerCase().trim())))
                    }}
                    className="text-xs"
                  >
                    Add All
                  </Button>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-3 space-y-2">
                  {toShow.map((writer, idx) => {
                    const suggestedIdx = suggestedWritersForEdit.findIndex(
                      (w) => w.name.toLowerCase().trim() === writer.name.toLowerCase().trim()
                    )
                    return (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{writer.name}</span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">({writer.role})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Add this specific writer to publishing contributors
                            const existingNames = new Set(
                              publishingContributors.map((c) => c.name.toLowerCase().trim()).filter(Boolean)
                            )
                            if (!existingNames.has(writer.name.toLowerCase().trim())) {
                              setPublishingContributors((prev) => [
                                ...prev,
                                {
                                  name: writer.name,
                                  role: writer.role || 'Writer',
                                  percentage: '',
                                  pro: '',
                                  ipiNumber: '',
                                  publishingLine: '',
                                },
                              ])
                            }
                            // Remove from suggested
                            setSuggestedWritersForEdit((prev) => prev.filter((_, i) => i !== suggestedIdx))
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Remove from suggested without adding
                            setSuggestedWritersForEdit((prev) => prev.filter((_, i) => i !== suggestedIdx))
                          }}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )
                  })}
                </div>
              </div>
              )
            })()}

            {/* Publishing Contributors */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Publishing Contributors</label>
                <button
                  type="button"
                  onClick={() =>
                    setPublishingContributors((prev) => [
                      ...prev,
                      { name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' },
                    ])
                  }
                  className="text-xs text-gray-600 hover:text-gray-900"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {publishingContributors.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input
                      className="ds-input col-span-3"
                      placeholder="Name"
                      value={c.name}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-2"
                      placeholder="Role"
                      value={c.role}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, role: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-1"
                      placeholder="%"
                      value={c.percentage}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, percentage: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-2"
                      placeholder="PRO"
                      value={c.pro}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, pro: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-2"
                      placeholder="IPI"
                      value={c.ipiNumber}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, ipiNumber: e.target.value } : x)))}
                    />
                    <input
                      className="ds-input col-span-1"
                      placeholder="Line"
                      value={c.publishingLine}
                      onChange={(e) => setPublishingContributors((prev) => prev.map((x, i) => (i === idx ? { ...x, publishingLine: e.target.value } : x)))}
                    />
                    <button
                      type="button"
                      className="col-span-1 flex items-center justify-center min-w-[2rem] text-red-600 hover:text-red-800"
                      onClick={() => setPublishingContributors((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Saving…' : (isEditing ? 'Save Changes' : 'Create Song')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setIsAddOpen(false)
                  resetAddModal()
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : addView === 'single' ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Search Spotify</label>
                <button
                  type="button"
                  onClick={goBackToOptions}
                  className="text-xs text-gray-600 hover:text-gray-900"
                >
                  Back to options
                </button>
              </div>
              <input
                type="text"
                value={singleQuery}
                onChange={(e) => setSingleQuery(e.target.value)}
                placeholder="Search by song title or artist"
                className="ds-input"
              />
            </div>

            {singleResults.length > 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm max-h-64 overflow-y-auto">
                {singleResults.slice(0, 10).map((track: any, idx: number) => (
                  <button
                    key={track.id || idx}
                    type="button"
                    onClick={() => {
                      setSingleSelected(track)
                      setSingleResults([])
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-md bg-gray-100 overflow-hidden flex-shrink-0">
                      {track.album?.images?.[0]?.url ? (
                        <img
                          src={track.album.images[0].url}
                          alt={track.name}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5 min-w-0">
                        <SpotifyBadge />
                        <span className="truncate min-w-0">{track.name || track.song_title}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {(track.artists || []).map((artist: any) => artist.name).filter(Boolean).join(', ') ||
                          track.artist_name ||
                          'Unknown artist'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}

            {singleSelected ? (
              <div className="rounded-lg border border-gray-200 p-4 flex items-center gap-4">
                <div className="h-14 w-14 rounded-md bg-gray-100 overflow-hidden flex-shrink-0">
                  {singleSelected.album?.images?.[0]?.url ? (
                    <img
                      src={singleSelected.album.images[0].url}
                      alt={singleSelected.name}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Ready to import</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {singleSelected.name} • {(singleSelected.artists || []).map((a: any) => a.name).filter(Boolean).join(', ')}
                  </div>
                </div>
                <div>
                  <Button type="button" onClick={handleSingleImport} disabled={singleImporting}>
                    {singleImporting ? 'Importing…' : 'Import Song'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : addView === 'bulk' ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Artist Name</label>
                <button
                  type="button"
                  onClick={goBackToOptions}
                  className="text-xs text-gray-600 hover:text-gray-900"
                >
                  Back to options
                </button>
              </div>
              <input
                type="text"
                value={bulkArtistName}
                onChange={(e) => {
                  setBulkArtistName(e.target.value)
                  setBulkArtistSelected(null)
                }}
                placeholder="Enter artist name"
                className="ds-input"
              />
              {bulkArtistSearching ? (
                <div className="text-xs text-gray-500 mt-2">Searching artists…</div>
              ) : null}
              {bulkArtistResults.length > 0 ? (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                  {bulkArtistResults.slice(0, 6).map((artist: any, idx: number) => (
                    <button
                      key={`${artist.spotifyUrl || artist.name || 'artist'}-${idx}`}
                      type="button"
                      onClick={() => {
                        setBulkArtistName(artist.name || '')
                        setBulkSpotifyUrl(artist.spotifyUrl || '')
                        setBulkArtistSelected(artist)
                        setBulkArtistResults([])
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                        {artist.image ? (
                          <img src={artist.image} alt={artist.name} className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {artist.name || 'Unknown artist'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {artist.followers ? `${artist.followers.toLocaleString()} followers` : 'Spotify artist'}
                        </div>
                      </div>
                      {artist.verified ? (
                        <span className="text-[10px] px-2 py-1 rounded bg-green-50 text-green-700">Verified</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="text-center text-xs text-gray-400">OR</div>
            <div>
              <label className="block text-sm font-medium mb-2">Spotify Link</label>
              <input
                type="text"
                value={bulkSpotifyUrl}
                onChange={(e) => setBulkSpotifyUrl(e.target.value)}
                placeholder="https://open.spotify.com/artist/..."
                className="ds-input"
              />
            </div>
            <Button
              type="button"
              onClick={handleBulkDiscover}
              disabled={bulkLoading || (!bulkArtistName.trim() && !bulkSpotifyUrl.trim())}
            >
              {bulkLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {discoveryProgress.message}
                </span>
              ) : (
                'Discover Catalog'
              )}
            </Button>

            {bulkResults.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-[rgb(var(--muted-rgb))]">{bulkResults.length} tracks found</p>
                <div className="max-h-[260px] overflow-y-auto space-y-2">
                  {bulkResults.slice(0, 20).map((track: any, idx: number) => (
                    <div
                      key={track.isrc || track.id || idx}
                      className="rounded-[var(--radius-md)] border border-[rgb(var(--border-rgb))] p-3 flex items-center gap-3 bg-[rgb(var(--surface-2-rgb))]"
                    >
                      <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0 bg-[rgb(var(--border-rgb))]">
                        {track.album_artwork || track.albumArtwork ? (
                          <img
                            src={track.album_artwork || track.albumArtwork}
                            alt={track.song_title || track.title || track.name}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[rgb(var(--text-rgb))] flex items-center gap-1.5 min-w-0">
                          <SpotifyBadge />
                          <span className="truncate min-w-0">{track.song_title || track.title || track.name || 'Untitled'}</span>
                        </div>
                        <p className="text-xs text-[rgb(var(--muted-rgb))] truncate">
                          {track.artist_name || track.artist || track.primary_artist || 'Unknown artist'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-[var(--radius-md)] border border-[rgb(var(--border-rgb))] p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[rgb(var(--text-rgb))]">Ready to import</p>
                    <p className="text-xs text-[rgb(var(--muted-rgb))] mt-0.5">
                      {bulkResults.length} tracks will be added to your catalog.
                    </p>
                    {bulkImporting ? (
                      <div className="mt-3 space-y-2">
                        <div className="flex justify-between text-xs text-[rgb(var(--muted-rgb))]">
                          <span>
                            {bulkPhase === 'saving' && 'Saving to catalog…'}
                            {bulkPhase === 'updating' && 'Enriching track details…'}
                            {bulkPhase === 'done' && 'Done'}
                            {!bulkPhase && 'Progress'}
                          </span>
                          <span>
                            {bulkPhase === 'done'
                              ? '100%'
                              : bulkProgress.total
                                ? `${Math.min(99, Math.round((bulkProgress.current / bulkProgress.total) * 100))}%`
                                : '0%'}
                          </span>
                        </div>
                        <div
                          className="w-full h-2 rounded-full overflow-hidden bg-[rgb(var(--border-rgb))]"
                          role="progressbar"
                          aria-valuenow={bulkPhase === 'done' ? bulkProgress.total : bulkProgress.current}
                          aria-valuemin={0}
                          aria-valuemax={bulkProgress.total}
                        >
                          <div
                            className="h-full rounded-full bg-[rgb(var(--text-rgb))] transition-all duration-300"
                            style={{
                              width: `${
                                bulkPhase === 'done'
                                  ? 100
                                  : bulkProgress.total
                                    ? Math.min(99, (bulkProgress.current / bulkProgress.total) * 100)
                                    : 0
                              }%`,
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-[rgb(var(--muted-rgb))]">
                          <span>
                            {bulkPhase === 'updating'
                              ? ''
                              : bulkPhase === 'done'
                                ? `${bulkProgress.current} / ${bulkProgress.total} tracks imported`
                                : `${bulkProgress.current} / ${bulkProgress.total} tracks saved`}
                          </span>
                          {bulkProgress.totalBatches > 1 && bulkPhase === 'saving' && (
                            <span>Batch {bulkProgress.currentBatch} / {bulkProgress.totalBatches}</span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <Button type="button" onClick={handleBulkImportAll} disabled={bulkImporting} className="flex-shrink-0">
                    {bulkImporting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {bulkPhase === 'updating' ? 'Enriching…' : 'Importing…'}
                      </span>
                    ) : (
                      'Import All'
                    )}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Credits</div>
              <button
                type="button"
                onClick={goBackToOptions}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            {creditsSongs.length === 0 ? (
              <div className="text-sm text-gray-500">No songs found yet.</div>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto">
                {creditsSongs.map((song) => (
                  <div key={song.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="text-sm font-medium text-gray-900">
                      {song.song_title || song.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {song.primary_artist || song.artist || 'Unknown artist'}
                    </div>
                    <div className="mt-3 space-y-2">
                      <div>
                        <div className="text-xs font-medium text-gray-700">Master Contributors</div>
                        {song.master_contributors && song.master_contributors.length > 0 ? (
                          <div className="text-xs text-gray-500">
                            {song.master_contributors.map((c) => c.name).filter(Boolean).join(', ')}
                          </div>
                        ) : song.producer ? (
                          <div className="text-xs text-gray-500">{song.producer}</div>
                        ) : (
                          <div className="text-xs text-gray-400">No master contributors</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-700">Publishing Contributors</div>
                        {song.publishing_contributors && song.publishing_contributors.length > 0 ? (
                          <div className="text-xs text-gray-500">
                            {song.publishing_contributors.map((c) => c.name).filter(Boolean).join(', ')}
                          </div>
                        ) : song.songwriter ? (
                          <div className="text-xs text-gray-500">{song.songwriter}</div>
                        ) : (
                          <div className="text-xs text-gray-400">No publishing contributors</div>
                        )}
                      </div>
                      {(() => {
                        const publishingNameSet = new Set(
                          (song.publishing_contributors || [])
                            .map((c) => normalizeCreditName(c?.name))
                            .filter(Boolean)
                        )
                        const remainingSuggested = (song.suggested_writers || []).filter(
                          (w) => w?.name && !publishingNameSet.has(normalizeCreditName(w.name))
                        )
                        if (remainingSuggested.length === 0) return null
                        return (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-3 mt-2">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                Suggested Writers
                                {song.split_status === 'unconfirmed' && remainingSuggested.length > 0 && (
                                  <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(Unconfirmed)</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                These songwriter credits were automatically pulled. Please review and confirm.
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={async () => {
                                // Navigate to edit mode with this song
                                startEditSong(song)
                                setOpenDropdown(null)
                              }}
                              className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Review & Add
                            </button>
                          </div>
                          <div className="text-xs text-gray-700 dark:text-gray-300">
                            {remainingSuggested.map((w) => w.name).filter(Boolean).join(', ')}
                          </div>
                        </div>
                        )
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Next Up</div>
          <div className="text-2xl font-semibold text-gray-900">
            {getDaysUntilRelease(nextUpReleaseDate)} {getDaysUntilRelease(nextUpReleaseDate) === 1 ? 'day' : 'days'}
          </div>
          <div className="text-xs text-gray-500 mt-1 truncate">
            {nextUpSong.song_title || nextUpSong.title} • {nextUpSong.primary_artist || nextUpSong.artist}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Total Streams</div>
          <div className="text-2xl font-semibold text-gray-900">{streamsTotal.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Streams Today</div>
          <div className="text-2xl font-semibold text-gray-900">{streamsToday.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Streams This Week</div>
          <div className="text-2xl font-semibold text-gray-900">{streamsWeek.toLocaleString()}</div>
        </div>
      </div>

      {/* Search & Filters (above table) */}
      {songs.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, artist, or ISRC..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <select
            id="music-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          >
            <option value="">All</option>
            <option value="demo">Demo</option>
            <option value="unreleased">Upcoming</option>
            <option value="released">Released</option>
          </select>
          {(searchQuery.trim() || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('')
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear filters
            </button>
          )}
          <span className="text-sm text-gray-500">
            {filteredSongs.length} of {songs.length} song{songs.length === 1 ? '' : 's'}
          </span>
          {filteredSongs.length > 0 ? (
            <div className="flex items-center gap-3 ml-auto">
              {selectedSongIds.size === 0 ? (
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:underline"
                >
                  Select all
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedSongIds(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                  >
                    Clear
                  </button>
                  <Button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 px-3"
                  >
                    {isBulkDeleting ? 'Deleting…' : `Delete ${selectedSongIds.size} selected`}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Songs Table */}
      {songs.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible mb-6">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full border-collapse min-w-0">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Title</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Artist</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Release Date</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Total Streams</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Today</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">This Week</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Works</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSongs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-sm text-gray-500">
                    No songs match your search or filters. Try adjusting or{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('')
                        setStatusFilter('')
                      }}
                      className="text-gray-900 font-medium hover:underline"
                    >
                      clear filters
                    </button>
                    .
                  </td>
                </tr>
              ) : (
              filteredSongs.map((song) => {
                const displayStreams = Number(
                  song.streams_manual_override ??
                    song.streams_total ??
                    song.soundcharts_data?.streams ??
                    song.soundcharts_data?.monthly_listeners ??
                    0
                )
                const isSelected = selectedSongIds.has(song.id)
                const publishingNameSet = new Set(
                  (song.publishing_contributors || [])
                    .map((c) => normalizeCreditName(c?.name))
                    .filter(Boolean)
                )
                const remainingSuggestedCount = (song.suggested_writers || []).filter(
                  (w) => w?.name && !publishingNameSet.has(normalizeCreditName(w.name))
                ).length
                return (
                  <tr
                    key={song.id}
                    className={`transition-colors hover:bg-gray-50/50 dark:hover:bg-white/[0.04] cursor-pointer ${isSelected ? 'bg-gray-100 dark:bg-white/[0.08]' : ''}`}
                    onClick={() => toggleSongSelection(song.id)}
                  >
                    <td className="py-4 px-4 text-sm font-medium text-gray-900 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-gray-100 overflow-hidden flex-shrink-0">
                          {song.artwork ? (
                            <img
                              src={song.artwork}
                              alt={song.song_title || song.title || 'Album artwork'}
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <SpotifyBadge />
                            <span className="truncate min-w-0">{song.song_title || song.title}</span>
                            {remainingSuggestedCount > 0 && song.split_status === 'unconfirmed' && (
                              <span
                                className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded"
                                title={`${remainingSuggestedCount} unconfirmed writer(s) - review and confirm`}
                              >
                                {remainingSuggestedCount} writer{remainingSuggestedCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {song.isrc ? (
                            <div className="text-xs text-gray-500 mt-1">ISRC: {song.isrc}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 align-middle">{song.primary_artist || song.artist}</td>
                    <td className="py-4 px-4 align-middle" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={song.status || 'unreleased'}
                        onChange={(e) => handleStatusChange(song.id, e.target.value as 'demo' | 'unreleased' | 'released')}
                        className={`text-xs font-medium px-2 py-1 rounded border-0 focus:ring-2 focus:ring-gray-900 ${
                          song.status === 'released'
                            ? 'bg-green-50 text-green-700'
                            : song.status === 'unreleased'
                              ? 'bg-yellow-50 text-yellow-700'
                              : 'bg-gray-50 text-gray-700'
                        }`}
                      >
                        <option value="demo">Demo</option>
                        <option value="unreleased">Upcoming</option>
                        <option value="released">Released</option>
                      </select>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 align-middle">
                      {song.release_date ? new Date(song.release_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-900 font-medium align-middle">
                      {displayStreams.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-600 align-middle">
                      {(Number(song.streams_today ?? 0) || 0).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-600 align-middle">
                      {(Number(song.streams_week ?? 0) || 0).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-center relative align-middle" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (openDropdown === song.id) {
                              setOpenDropdown(null)
                              setDropdownPosition(null)
                              return
                            }
                            const rect = e.currentTarget.getBoundingClientRect()
                            setDropdownPosition({
                              top: rect.bottom + 8,
                              right: window.innerWidth - rect.right
                            })
                            setOpenDropdown(song.id)
                          }}
                          className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 rounded p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        {openDropdown === song.id && dropdownPosition && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => {
                                setOpenDropdown(null)
                                setDropdownPosition(null)
                              }}
                            />
                            <div 
                              className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
                              style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
                            >
                              <button
                                onClick={() => {
                                  setCreditsSongId(song.id)
                                  setAddView('credits')
                                  setIsAddOpen(true)
                                  setOpenDropdown(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                View Works
                              </button>
                              {song.isrc && (
                                <button
                                  onClick={() => {
                                    handleFetchCredits(song)
                                    setOpenDropdown(null)
                                  }}
                                  disabled={fetchingMusoCredits === song.id}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                  {fetchingMusoCredits === song.id ? 'Fetching Credits...' : 'Fetch Credits'}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  startEditSong(song)
                                  setOpenDropdown(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteSong(song.id)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-sm text-gray-500">No songs found</p>
        </div>
      )}
    </div>
  )
}
